/* eslint-disable react/jsx-no-target-blank */
import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import axios from 'axios'
import { Spinner } from 'react-bootstrap'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import Header from '../components/layout/Header'
import Footer from '../components/layout/Footer'
import { getBrochureSlug, slugifyTitle } from '../utils/slugify'
import { FiletypePdf, TelephoneFill, Whatsapp } from 'react-bootstrap-icons'

// ─── Download icon ─────────────────────────────────────────────────────────
function DownloadIcon({ size = 14 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
                d="M8 2v8M5 7l3 3 3-3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path d="M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    )
}

// ─── BrochureCard subcomponent ─────────────────────────────────────────────
function BrochureCard({ brochureItem, index, onDownload }) {
    const [hovered, setHovered] = useState(false)
    const [downloading, setDownloading] = useState(false)

    const handleDownloadClick = async (e) => {
        e.stopPropagation()
        setDownloading(true)
        await onDownload(brochureItem)
        setDownloading(false)
    }

    const filename = brochureItem?.filename || brochureItem?.file || brochureItem?.fileName
    const displayName = brochureItem?.original_name || brochureItem?.originalName || filename || 'Project Document'

    // Direct link to the source file endpoint for target="_blank"
    const fileUrl = brochureItem?.url

    return (
        <div
            style={{
                ...detailStyles.brochureCard,
                ...(hovered && !downloading ? detailStyles.brochureCardHover : {}),
                animationDelay: `${index * 60}ms`,
            }}
            className="mb-3 mb-md-2 brochure-card-animate"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div style={detailStyles.brochureIndex}>{String(index + 1).padStart(2, '0')}</div>

            <div style={detailStyles.brochureIconWrap}>
                <FiletypePdf className="fs-ss-24" />
            </div>

            <div style={detailStyles.brochureInfo}>
                {/* PDF title wrapped in a direct target="_blank" anchor link */}
                <a href={fileUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    <div style={detailStyles.brochureName} className="pdf-title-link" title={displayName}>
                        {displayName}
                    </div>
                </a>
                <div style={detailStyles.brochureMeta}>
                    <span>{brochureItem?.fileSize ? brochureItem?.fileSize : ''}</span>
                    <span style={detailStyles.metaDot}>·</span>
                    {(brochureItem?.updatedAt || brochureItem?.createdAt || brochureItem?.updated_at) && (
                        <div style={{ ...detailStyles.brochureMeta, marginTop: '2px' }}>
                            <span>
                                Updated{' '}
                                {new Date(
                                    brochureItem.updatedAt || brochureItem.createdAt || brochureItem.updated_at,
                                ).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <button
                className="border-white rounded-2"
                style={{
                    ...detailStyles.downloadBtn,
                    ...(downloading ? detailStyles.downloadBtnLoading : {}),
                    ...(hovered && !downloading ? detailStyles.downloadBtnHover : {}),
                }}
                onClick={handleDownloadClick}
                aria-label={`Download document`}
                disabled={downloading || !filename}
            >
                {downloading ? (
                    <>
                        <svg
                            width="13"
                            height="13"
                            viewBox="0 0 16 16"
                            fill="none"
                            style={{ animation: 'spin 0.8s linear infinite' }}
                            aria-hidden="true"
                            className="me-1"
                        >
                            <circle
                                cx="8"
                                cy="8"
                                r="6"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeDasharray="20 8"
                            />
                        </svg>
                        Downloading
                    </>
                ) : (
                    <>
                        <DownloadIcon />
                        <span style={{ marginLeft: '6px' }}>Download</span>
                    </>
                )}
            </button>
        </div>
    )
}

const brochureMatchesSlug = (item, slug) => {
    if (!item || !slug) return false
    if (item.slug === slug) return true
    return slugifyTitle(item.title) === slug
}

// ─── Main BrochureDetail Component ──────────────────────────────────────────
const BrochureDetail = () => {
    const { slug } = useParams()
    const [brochure, setBrochure] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [imgLoaded, setImgLoaded] = useState(false)
    const [downloadingAll, setDownloadingAll] = useState(false)
    const [downloadAllHovered, setDownloadAllHovered] = useState(false)

    useEffect(() => {
        window.scrollTo(0, 0)

        const fetchBrochure = async () => {
            setLoading(true)
            setError(null)

            try {
                try {
                    const res = await axios.get(`${process.env.REACT_APP_API_URL}/brochures/slug/${slug}`)
                    if (res.data) {
                        setBrochure(res.data)
                        return
                    }
                } catch (slugErr) {
                    console.log('Slug endpoint unavailable, falling back to list:', slugErr)
                }

                const res = await axios.get(`${process.env.REACT_APP_API_URL}/brochures/website`)
                const list = res.data?.brochures || []
                const found = list.find((item) => brochureMatchesSlug(item, slug))
                if (found) {
                    setBrochure(found)
                } else {
                    setError('Brochure information not found.')
                }
            } catch (err) {
                console.log(err)
                setError('Unable to load brochure architectural records.')
            } finally {
                setLoading(false)
            }
        }

        if (slug) {
            fetchBrochure()
        } else {
            setError('Brochure address link broken.')
            setLoading(false)
        }
    }, [slug])

    const runFileDownload = useCallback(async (targetItem) => {
        const filename = targetItem?.filename || targetItem?.file || targetItem?.fileName
        if (!filename) return

        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/brochures/file/${filename}`, {
                responseType: 'blob',
            })
            const url = window.URL.createObjectURL(res.data)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute(
                'download',
                targetItem?.original_name || targetItem?.originalName || targetItem?.title || filename,
            )
            document.body.appendChild(link)
            link.click()
            link.remove()
            setTimeout(() => window.URL.revokeObjectURL(url), 100)
        } catch (err) {
            console.log('Download handling issue occurs:', err)
        }
    }, [])

    const handleDownloadAll = async () => {
        if (!brochure) return
        const filesToDownload = brochure.files || [brochure]

        setDownloadingAll(true)
        const zip = new JSZip()

        try {
            for (const fileItem of filesToDownload) {
                const filename = fileItem?.filename || fileItem?.file || fileItem?.fileName
                if (!filename) continue

                const response = await axios.get(`${process.env.REACT_APP_API_URL}/brochures/file/${filename}`, {
                    responseType: 'arraybuffer',
                })

                const zipEntryName = fileItem?.original_name || fileItem?.originalName || fileItem?.title || filename
                zip.file(zipEntryName, response.data)
            }

            const content = await zip.generateAsync({ type: 'blob' })
            const zipName = `${slugifyTitle(brochure.title || 'documents')}-brochures.zip`

            saveAs(content, zipName)
        } catch (err) {
            console.error('Error creating ZIP archive compilation package:', err)
        } finally {
            setDownloadingAll(false)
        }
    }

    const brochureSlug = getBrochureSlug(brochure)

    const hasContent = (val) => {
        if (!val) return false
        const clean = val.replace(/<\/?[^>]+(>|$)/g, '').trim()
        return clean !== ''
    }

    if (loading) {
        return (
            <>
                <Header />
                <div
                    style={{
                        ...detailStyles.page,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '60vh',
                    }}
                >
                    <Spinner animation="border" variant="light" />
                </div>
                <Footer />
            </>
        )
    }

    if (error || !brochure) {
        return (
            <>
                <Header />
                <div style={{ ...detailStyles.page, padding: '100px 20px', textAlign: 'center' }}>
                    <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.7)', marginBottom: '24px' }}>
                        {error || 'Brochure records unavailable.'}
                    </p>
                    <Link to="/brochure" style={{ color: '#fff', textDecoration: 'underline', fontWeight: '500' }}>
                        &larr; Back to brochures catalog
                    </Link>
                </div>
                <Footer />
            </>
        )
    }

    const dynamicFilesList = brochure.files || []
    const paymentPlanDetails = brochure?.payment_plan_details || []

    return (
        <>
            <Helmet>
                <title>{brochure.title} | 800 Homes</title>
                <meta name="description" content={brochure.description || brochure.title} />
                <link rel="canonical" href={`https://800homes.ae/brochure/${brochureSlug}`} />
                <meta property="og:title" content={brochure.title} />
                <meta property="og:url" content={`https://800homes.ae/brochure/${brochureSlug}`} />
                {brochure.picture && <meta property="og:image" content={brochure.picture} />}
            </Helmet>

            <style>{detailGlobalCSS}</style>

            <Header />

            <div style={detailStyles.page}>
                <div style={detailStyles.heroWrap}>
                    {brochure.picture ? (
                        <img
                            src={brochure.picture}
                            alt={brochure.title}
                            style={{ ...detailStyles.heroImg, opacity: imgLoaded ? 1 : 0 }}
                            onLoad={() => setImgLoaded(true)}
                        />
                    ) : (
                        <div
                            style={{
                                ...detailStyles.heroImg,
                                background: 'linear-gradient(140deg, #1e293b 0%, #0f172a 100%)',
                            }}
                        />
                    )}
                    <div style={detailStyles.heroOverlay} />

                    <div style={detailStyles.heroContent}>
                        <span style={{ ...detailStyles.statusPill, ...detailStyles.pillReady }}>
                            {brochure.type?.trim() || 'Premium Asset'}
                        </span>

                        {/* Static presentation header component */}
                        <h1 style={detailStyles.heroTitle}>{brochure.title}</h1>

                        <p style={detailStyles.heroLocation}>
                            <svg
                                width="11"
                                height="14"
                                viewBox="0 0 12 16"
                                fill="none"
                                aria-hidden="true"
                                style={{ marginRight: 7, verticalAlign: 'middle' }}
                            >
                                <path
                                    d="M6 0C3.24 0 1 2.24 1 5c0 3.75 5 11 5 11s5-7.25 5-11c0-2.76-2.24-5-5-5zm0 6.5A1.5 1.5 0 1 1 6 3.5a1.5 1.5 0 0 1 0 3z"
                                    fill="rgba(255,255,255,0.7)"
                                />
                            </svg>
                            {brochure.location || 'Dubai, United Arab Emirates'}
                        </p>
                    </div>
                </div>

                <div style={detailStyles.mainWrap}>
                    <div style={detailStyles.mainInner} className="detail-main-inner pt-4 pt-md-0 row">
                        <div className="col-md-7" style={detailStyles.leftCol}>
                            <div style={detailStyles.statsRow} className="detail-stats-row">
                                {[
                                    { label: 'Available Units', value: brochure.available_units },
                                    { label: 'Starting Price', value: brochure.starting_price },
                                    { label: 'Handover', value: brochure.handover },
                                    { label: 'Developer', value: brochure.developer },
                                    { label: 'Payment Plan', value: brochure.payment_plan },
                                ]
                                    .filter((stat) => stat.value && stat.value.toString().trim() !== '')
                                    .map((stat, index) => (
                                        <div key={index} style={detailStyles.statCard}>
                                            <div className="pb-2" style={detailStyles.statLabel}>
                                                {stat.label}
                                            </div>
                                            <div style={detailStyles.statValue}>{stat.value}</div>
                                        </div>
                                    ))}
                            </div>

                            {hasContent(brochure.description) && (
                                <section className="pt-4 pt-md-0" style={detailStyles.section}>
                                    <h2 style={detailStyles.sectionTitle}>About this project</h2>
                                    <p style={detailStyles.description}>{brochure.description}</p>
                                </section>
                            )}

                            <section className="pt-4 pt-md-0" style={detailStyles.section}>
                                <div style={detailStyles.sectionHeader}>
                                    <h2 className="mt-3 mt-md-0" style={detailStyles.sectionTitle}>
                                        Brochures & Documents
                                    </h2>
                                    <span style={detailStyles.brochureCountBadge}>
                                        {dynamicFilesList.length} {dynamicFilesList.length === 1 ? 'file' : 'files'}
                                    </span>
                                </div>
                                <div style={detailStyles.brochureList}>
                                    {dynamicFilesList.length > 0 ? (
                                        dynamicFilesList.map((fileItem, idx) => (
                                            <BrochureCard
                                                key={idx}
                                                brochureItem={fileItem}
                                                index={idx}
                                                onDownload={runFileDownload}
                                            />
                                        ))
                                    ) : (
                                        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>
                                            No dynamic file media packages attached to this property.
                                        </p>
                                    )}
                                </div>
                            </section>

                            {hasContent(brochure.long_description) && (
                                <section className="pt-4 pt-md-0" style={detailStyles.projectDetailsSection}>
                                    <h2 style={detailStyles.sectionTitle}>Project details</h2>
                                    <div
                                        className="project-details-content"
                                        style={{ ...detailStyles.description, marginTop: '20px' }}
                                        dangerouslySetInnerHTML={{ __html: brochure.long_description }}
                                    />
                                </section>
                            )}

                            {paymentPlanDetails.length > 0 && (
                                <div className="mt-4 mt-md-0">
                                    <h2 style={detailStyles.sectionTitle}>Payment Plans</h2>

                                    <div style={detailStyles.statsRow} className="detail-stats-row">
                                        {paymentPlanDetails.map((plan, index) => (
                                            <div key={index} style={detailStyles.statCard}>
                                                <div className="pb-2" style={detailStyles.statLabel}>
                                                    {plan.title}
                                                </div>

                                                <div className="" style={detailStyles.statValueInstallments}>
                                                    {plan.value}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {brochure?.amenities?.length > 0 && (
                                <section className="py-4 pb-md-5 mb-4" style={detailStyles.sectionAmenities}>
                                    <h2 style={detailStyles.sectionTitle}>Amenities</h2>

                                    <div style={detailStyles.statsRow} className="detail-stats-row">
                                        {brochure.amenities.map((amenity) => (
                                            <div key={amenity._id}>
                                                <div className="d-flex align-items-center justify-content-start mb-3">
                                                    {amenity.icon && (
                                                        <img
                                                            src={amenity.icon}
                                                            alt={amenity.title}
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                objectFit: 'contain',
                                                            }}
                                                            className="bg-light p-2 rounded-2 me-3"
                                                        />
                                                    )}

                                                    <div className="fw-300 fs-ss-15">{amenity.title}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </div>

                        <aside style={detailStyles.sidebar} className="detail-sidebar">
                            <div style={detailStyles.sidebarStickyWrapper}>
                                <div style={detailStyles.sideCard}>
                                    <div style={detailStyles.agentRow}>
                                        <div>
                                            <div className="fs-ss-26 fw-500 lh-sm text-white">Get in touch</div>
                                        </div>
                                    </div>

                                    <p style={detailStyles.sideNote}>
                                        Speak to our agent for the latest availability, pricing and payment plan options
                                        for this project.
                                    </p>

                                    <a href="tel:80046637" style={{ textDecoration: 'none' }}>
                                        <div className="text-decoration-none px-4 py-ss-2 text-center text-white bg-ss-primary bg-hover-ss-primary rounded-1 mb-3">
                                            <TelephoneFill className="text-white me-2 mb-1" />
                                            <span> Call Now </span>
                                        </div>
                                    </a>

                                    <a
                                        href="https://api.whatsapp.com/send/?phone=971585746637"
                                        target="_blank"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <button style={detailStyles.ctaWhatsApp}>
                                            <Whatsapp className="me-2" style={{ color: '#25D366' }} />
                                            WhatsApp
                                        </button>
                                    </a>
                                </div>

                                <div className="mb-5 mb-md-0" style={detailStyles.imageDownloadAllCard}>
                                    <div style={detailStyles.imageDownloadTextContainer}>
                                        <div style={detailStyles.imageDownloadTitle}>Download all</div>
                                        <div style={detailStyles.imageDownloadSubTitle}>
                                            {dynamicFilesList.length > 0
                                                ? `${dynamicFilesList.length} file${dynamicFilesList.length > 1 ? 's' : ''} as a ZIP archive`
                                                : '0 files available'}
                                        </div>
                                    </div>
                                    <button
                                        style={{
                                            ...detailStyles.imageDownloadCircleBtn,
                                            ...(downloadAllHovered ? detailStyles.imageDownloadCircleBtnHover : {}),
                                            ...(downloadingAll ? detailStyles.downloadBtnLoading : {}),
                                        }}
                                        onClick={handleDownloadAll}
                                        disabled={downloadingAll || dynamicFilesList.length === 0}
                                        onMouseEnter={() => setDownloadAllHovered(true)}
                                        onMouseLeave={() => setDownloadAllHovered(false)}
                                        aria-label="Download all archives"
                                    >
                                        {downloadingAll ? (
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                style={{ animation: 'spin 0.8s linear infinite' }}
                                            >
                                                <circle
                                                    cx="8"
                                                    cy="8"
                                                    r="6"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeDasharray="20 8"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                                <polyline points="19 12 12 19 5 12"></polyline>
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    )
}

// ─── Global Inline CSS Fixes ────────────────────────────────────────────────
const detailGlobalCSS = `
    .project-details-content,
    .project-details-content *,
    .project-details-content span,
    .project-details-content p,
    .project-details-content div {
        color: rgba(255, 255, 255, 0.8) !important;
    }

    .pdf-title-link {
        cursor: pointer;
        transition: opacity 0.2s ease, text-decoration 0.2s ease;
    }

    .pdf-title-link:hover {
        opacity: 0.8;
        text-decoration: underline !important;
    }

    .brochure-card-animate {
        animation: fadeSlideUp 0.35s ease both;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    @keyframes fadeSlideUp {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }

    /* Tablet and below */
    @media (max-width: 960px) {
        .detail-main-inner { 
            flex-direction: column !important; 
            gap: 40px !important; 
        }
        .detail-sidebar { 
            width: 100% !important; 
            position: static !important; 
        }
    }

    /* Mobile - Large screens (768px to 960px) */
    @media (max-width: 768px) {
        .detail-stats-row { 
            grid-template-columns: repeat(2, 1fr) !important; 
            gap: 12px !important; 
        }
        .detail-main-inner {
            gap: 32px !important;
        }
    }

    /* Mobile - Medium screens (480px to 768px) */
    @media (max-width: 600px) {
        .detail-stats-row { 
            grid-template-columns: repeat(2, 1fr) !important; 
            gap: 10px !important; 
        }
        .detail-sidebar {
            gap: 16px !important;
        }
        .brochure-card-container {
            gap: 10px !important;
        }
        .brochure-card-animate {
            flex-wrap: wrap !important;
        }
        .brochure-card-animate button {
            flex-basis: 100% !important;
            width: 100% !important;
        }
        .detail-main-inner {
            gap: 24px !important;
        }
    }

    /* Mobile - Small screens (below 480px) */
    @media (max-width: 480px) {
        .detail-stats-row { 
            grid-template-columns: repeat(2, 1fr) !important; 
            gap: 8px !important; 
        }
    }
`

// ─── Refined Style Tokens ────────────────────────────────────────────────────
const detailStyles = {
    page: {
        minHeight: '100vh',
        color: '#ffffff',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
    },
    heroWrap: {
        position: 'relative',
        height: 'clamp(240px, 50vw, 340px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: 'clamp(20px, 5vw, 40px) clamp(16px, 5vw, 32px)',
        background: 'transparent',
    },
    heroImg: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'opacity 0.6s ease',
        zIndex: 1,
    },
    heroOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.8) 100%)',
        zIndex: 2,
    },
    heroContent: {
        position: 'relative',
        zIndex: 3,
        maxWidth: '1136px',
        width: '100%',
        margin: '0 auto',
    },
    statusPill: {
        display: 'inline-block',
        padding: '10px 10px 5px 10px',
        fontSize: '11px',
        fontWeight: 400,
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        borderRadius: '4px',
        marginBottom: '12px',
    },
    pillReady: {
        background: 'rgba(255, 255, 255, 0.15)',
        color: '#ffffff',
        backdropFilter: 'blur(4px)',
        border: '1px solid rgba(255, 255, 255, 0.25)',
    },
    heroTitle: {
        fontSize: 'clamp(24px, 6vw, 38px)',
        fontWeight: 500,
        color: '#fff',
        margin: '0 0 8px 0',
        lineHeight: 1.15,
    },
    heroLocation: {
        fontSize: '14px',
        color: 'rgba(255,255,255,0.75)',
        margin: 0,
        fontWeight: 300,
    },
    mainWrap: {
        width: '100%',
        padding: 'clamp(16px, 4vw, 48px) clamp(12px, 3vw, 32px)',
    },
    mainInner: {
        maxWidth: '1200px',
        margin: '0 auto',
        gap: 'clamp(32px, 5vw, 64px)',
    },
    leftCol: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(16px, 4vw, 40px)',
    },
    statsRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 'clamp(8px, 2vw, 16px)',
    },
    statCard: {
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: 'clamp(12px, 3vw, 16px) clamp(12px, 3vw, 20px)',
        borderRadius: '6px',
    },
    statValue: {
        fontSize: 'clamp(15px, 2.5vw, 14px)',
        fontWeight: 500,
        color: '#ffffff',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    },
    statValueInstallments: {
        fontSize: 'clamp(18px, 2.5vw, 14px)',
        fontWeight: 500,
        color: '#ffffff',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    },
    statLabel: {
        fontSize: 'clamp(14px, 2vw, 14px)',
        color: 'rgba(255, 255, 255, 0.6)',
        marginTop: '4px',
        fontWeight: '300',
    },
    section: {
        background: 'transparent',
        borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '0px',
        padding: '0 0 clamp(16px, 3vw, 32px) 0',
    },
    sectionAmenities: {
        background: 'transparent',
        borderRadius: '0px',
        padding: '0 0 clamp(16px, 3vw, 32px) 0',
    },
    projectDetailsSection: {
        background: 'transparent',
        borderRadius: '0px',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
    },
    sectionTitle: {
        fontSize: 'clamp(18px, 4vw, 24px)',
        fontWeight: 500,
        color: '#ffffff',
        margin: '0 0 16px 0',
    },
    brochureCountBadge: {
        fontSize: '12px',
        background: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '3px 8px',
        borderRadius: '12px',
        color: '#ffffff',
    },
    description: {
        fontSize: 'clamp(14px, 2.5vw, 15px)',
        lineHeight: '1.7',
        color: 'rgba(255, 255, 255, 0.8)',
        margin: 0,
        fontWeight: 300,
    },
    brochureList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(8px, 2vw, 12px)',
    },
    brochureCard: {
        display: 'flex',
        alignItems: 'center',
        padding: 'clamp(12px, 3vw, 16px) clamp(12px, 3vw, 20px)',
        background: 'transparent',
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        gap: 'clamp(6px, 1.5vw, 12px)',
        flexWrap: 'wrap',
        minHeight: '0',
    },
    brochureCardHover: {
        background: 'rgba(255, 255, 255, 0.05)',
        transform: 'translateY(-1px)',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25)',
    },
    brochureIndex: {
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.4)',
        marginRight: 'clamp(8px, 2vw, 16px)',
        display: 'none',
    },
    brochureIconWrap: {
        marginRight: 'clamp(8px, 2vw, 16px)',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
    },
    brochureInfo: {
        flex: 1,
        minWidth: 0,
        paddingRight: 'clamp(8px, 2vw, 16px)',
    },
    brochureName: {
        fontSize: 'clamp(14px, 2.5vw, 15px)',
        fontWeight: 500,
        color: '#ffffff',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    brochureMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.5)',
        marginTop: '4px',
    },
    metaDot: {
        opacity: 0.5,
    },
    downloadBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(6px, 2vw, 8px) clamp(10px, 2vw, 14px)',
        fontSize: 'clamp(12px, 2vw, 13px)',
        fontWeight: 500,
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '2px',
        color: '#ffffff',
        cursor: 'pointer',
        transition: 'all 0.2s',
        outline: 'none',
        whiteSpace: 'nowrap',
    },
    downloadBtnHover: {
        background: 'rgba(255, 255, 255, 0.1)',
        borderColor: '#ffffff',
        color: '#ffffff',
    },
    downloadBtnLoading: {
        background: 'transparent',
        color: 'rgba(255, 255, 255, 0.4)',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        cursor: 'not-allowed',
    },
    sidebar: {
        width: '320px',
        flexShrink: 0,
        '@media (max-width: 960px)': {
            width: '100%',
        },
    },
    sidebarStickyWrapper: {
        position: 'sticky',
        top: '100px',
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(16px, 3vw, 20px)',
        '@media (max-width: 768px)': {
            position: 'static',
            top: 'auto',
        },
    },
    sideCard: {
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.2)',
        borderRadius: '8px',
        padding: 'clamp(16px, 4vw, 28px)',
    },
    agentRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        marginBottom: '20px',
    },
    sideNote: {
        fontSize: 'clamp(13px, 2vw, 13px)',
        lineHeight: '1.6',
        color: 'rgba(255, 255, 255, 0.75)',
        margin: '0 0 24px 0',
    },
    ctaWhatsApp: {
        width: '100%',
        padding: '11px',
        background: 'transparent',
        color: '#ffffff',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '4px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
    },
    imageDownloadAllCard: {
        background: 'transparent',
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        padding: 'clamp(16px, 4vw, 22px) clamp(12px, 4vw, 24px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        gap: 'clamp(12px, 3vw, 16px)',
        flexWrap: 'wrap',
    },
    imageDownloadTextContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    imageDownloadTitle: {
        color: '#ffffff',
        fontSize: 'clamp(15px, 2.5vw, 15px)',
        fontWeight: '500',
        fontFamily: "'DM Sans', sans-serif",
    },
    imageDownloadSubTitle: {
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: 'clamp(13px, 2vw, 13px)',
        fontWeight: '400',
        fontFamily: "'DM Sans', sans-serif",
    },
    imageDownloadCircleBtn: {
        width: '35px',
        height: '35px',
        borderRadius: '50%',
        background: '#b78b4c',
        color: '#ffffff',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'transform 0.15s ease, background-color 0.2s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    },
    imageDownloadCircleBtnHover: {
        background: '#b78b4c',
        transform: 'scale(1.04)',
    },
}

export default BrochureDetail
